import { useState, type CSSProperties, type ReactNode } from 'react';
import { thStyle } from '@/components/ui';

export type SortDir = 'asc' | 'desc';
export type SortAccessor<T> = (row: T) => string | number | null | undefined;

export interface SortState<T> {
  key: string | null;
  dir: SortDir;
  toggle: (key: string) => void;
  sorted: T[];
}

/**
 * 通用表格排序：傳入資料與各欄位取值器，回傳已排序資料與目前排序狀態。
 * 點同一欄切換升/降冪，點不同欄改以該欄升冪。
 */
export function useSort<T>(
  rows: T[],
  accessors: Record<string, SortAccessor<T>>,
  initialKey?: string,
  initialDir: SortDir = 'asc',
): SortState<T> {
  const [key, setKey] = useState<string | null>(initialKey ?? null);
  const [dir, setDir] = useState<SortDir>(initialDir);

  const toggle = (k: string) => {
    if (key === k) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setKey(k);
      setDir('asc');
    }
  };

  let sorted = rows;
  const acc = key ? accessors[key] : undefined;
  if (acc) {
    sorted = [...rows].sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      let c: number;
      if (typeof va === 'number' && typeof vb === 'number') {
        c = va - vb;
      } else {
        c = String(va ?? '').localeCompare(String(vb ?? ''), 'zh-Hant-TW', {
          numeric: true,
        });
      }
      return dir === 'asc' ? c : -c;
    });
  }

  return { key, dir, toggle, sorted };
}

/** 可排序的表頭欄位。點擊切換排序，顯示 ↕ / ▲ / ▼ 指示。 */
export function SortTh<T>({
  label,
  sortKey,
  sort,
  align = 'left',
  style,
}: {
  label: ReactNode;
  sortKey: string;
  sort: SortState<T>;
  align?: 'left' | 'right' | 'center';
  style?: CSSProperties;
}) {
  const active = sort.key === sortKey;
  return (
    <th
      onClick={() => sort.toggle(sortKey)}
      style={{ ...thStyle, textAlign: align, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
    >
      {label}
      <span
        style={{
          marginLeft: 4,
          fontSize: 9,
          color: active ? 'var(--primary)' : 'var(--ink-4)',
        }}
      >
        {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </th>
  );
}
